import React, { useCallback, useEffect, useState } from "react";
import { Panel } from "@/components/common/Panel";
import { StatusBadge } from "@/components/common/Badges";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, Edit3, Save, X, ShieldCheck, KeyRound, UserCog, CheckCircle2, LayoutGrid } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";

const EXECUTION_ROLES = ["Execution_All", "Execution_CodeReview", "Execution_SU24Adjust", "Execution_ODataService"];

export default function Admin() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [audit, setAudit] = useState([]);
    const [editing, setEditing] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [customRoles, setCustomRoles] = useState([]);
    const [modulesList, setModulesList] = useState([]);
    const [editingRole, setEditingRole] = useState(null);
    const [createRoleOpen, setCreateRoleOpen] = useState(false);
    const isSuper = !!user?.is_super_admin;
    const isAdmin = (user?.roles || []).includes("Admin");

    const load = useCallback(async () => {
        try {
            const [u, a, cr, m] = await Promise.all([
                api.get("/auth/users"), api.get("/audit"),
                api.get("/admin/custom-roles"), api.get("/admin/modules"),
            ]);
            setUsers(u.data);
            setAudit(a.data.slice(0, 50));
            setCustomRoles(cr.data);
            setModulesList(m.data.modules);
        } catch (e) {
            console.warn("Admin data fetch failed (Admin role required)", e);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async (u) => {
        if (!confirm(`Delete user ${u.email}?`)) return;
        try {
            await api.delete(`/auth/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const removeCustomRole = async (r) => {
        if (!confirm(`Delete custom role "${r.name}"? Any user still holding this role name loses its module grants.`)) return;
        try {
            const { data } = await api.delete(`/admin/custom-roles/${r.id}`);
            toast.success(`Deleted · ${data.users_affected} user(s) affected`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    if (!isAdmin) {
        return (
            <Panel eyebrow="Restricted" title="Administration">
                <p className="text-sm text-slate-400">
                    You do not have the <span className="font-mono text-cyan-300">Admin</span> role.
                    User administration is reserved for platform owners.
                </p>
            </Panel>
        );
    }

    return (
        <div className="space-y-6">
            {/* Owner banner */}
            {isSuper && (
                <div className="panel-elevated p-4 flex items-center gap-3 border-l-2 border-cyan-400">
                    <ShieldCheck size={18} className="text-cyan-300" />
                    <div className="flex-1">
                        <div className="font-display text-sm text-white">Platform Owner Console</div>
                        <p className="text-xs text-slate-400">You may create, update, deactivate and delete users — including granting the Admin role.</p>
                    </div>
                </div>
            )}

            {/* Users table */}
            <Panel
                eyebrow={`${users.length} accounts`}
                title="User Administration"
                action={
                    <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} isSuper={isSuper} customRoles={customRoles} onCreated={load} />
                }
                testId="admin-users-panel"
            >
                <table className="tbl">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Mobile</th>
                            <th>Roles</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 && <tr><td colSpan={6} className="text-center text-xs text-slate-500 font-mono py-6">No users found.</td></tr>}
                        {users.map((u) => (
                            <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                                <td>
                                    <div className="text-white flex items-center gap-2">
                                        {u.name}
                                        {u.is_super_admin && (
                                            <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-cyan-500/40 text-cyan-300 rounded-sm">
                                                Owner
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="font-mono text-[12px] text-cyan-300/80">{u.email}</td>
                                <td className="font-mono text-[12px] text-slate-300">{u.mobile || "—"}</td>
                                <td>
                                    <div className="flex flex-wrap gap-1">
                                        {u.roles.map((r) => (
                                            <span key={r} className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${
                                                r === "Admin" ? "border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400"
                                            }`}>
                                                {r}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td><StatusBadge status={u.active ? "active" : "archived"} /></td>
                                <td className="text-right whitespace-nowrap">
                                    {!u.is_super_admin && (
                                        <>
                                            <button
                                                data-testid={`admin-edit-${u.id}`}
                                                onClick={() => setEditing(u)}
                                                className="btn-ghost mr-2 inline-flex items-center gap-1"
                                            >
                                                <Edit3 size={11} /> Edit
                                            </button>
                                            <button
                                                data-testid={`admin-delete-${u.id}`}
                                                onClick={() => remove(u)}
                                                className="btn-ghost text-rose-300 border-rose-500/30 hover:border-rose-500/60 inline-flex items-center gap-1"
                                            >
                                                <Trash2 size={11} /> Delete
                                            </button>
                                        </>
                                    )}
                                    {u.is_super_admin && <span className="text-[10px] font-mono uppercase text-slate-500">protected</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Panel>

            <Panel eyebrow="Audit" title="Platform Audit Log">
                <ul className="space-y-2 max-h-[420px] overflow-y-auto">
                    {audit.length === 0 && <li className="text-xs text-slate-500 font-mono">No audit events yet.</li>}
                    {audit.map((a) => (
                        <li key={a.id} className="panel px-3 py-2 text-xs flex items-center justify-between">
                            <div className="min-w-0 flex items-center gap-3">
                                <span className="font-mono text-cyan-300 uppercase tracking-wider text-[10px]">{a.action}</span>
                                <span className="text-slate-300">{a.entity_type}</span>
                                <span className="font-mono text-[10px] text-slate-500">{a.actor_email}</span>
                            </div>
                            <div className="text-[10px] font-mono text-slate-500">{new Date(a.created_at).toLocaleString()}</div>
                        </li>
                    ))}
                </ul>
            </Panel>

            {/* Dynamic RBAC: Custom Roles */}
            <Panel
                eyebrow={`${customRoles.length} custom roles`}
                title="Dynamic RBAC — Custom Roles"
                action={
                    <button data-testid={TID.adminCustomRoleCreateBtn} onClick={() => setCreateRoleOpen(true)} className="btn-primary flex items-center gap-1.5">
                        <Plus size={12} /> New Custom Role
                    </button>
                }
                testId={TID.adminCustomRolesPanel}
            >
                <p className="text-xs text-slate-500 font-mono mb-3">
                    Named roles that grant an explicit subset of platform modules — assign them to users below,
                    alongside (or instead of) the fixed Execution roles. <span className="text-cyan-300">Admin</span> and
                    <span className="text-cyan-300"> Execution_All</span> always retain access to every module.
                </p>
                {customRoles.length === 0 ? (
                    <p className="text-sm text-slate-500 font-mono py-4 text-center">No custom roles yet — create one to scope access to specific modules.</p>
                ) : (
                    <div className="space-y-2">
                        {customRoles.map((r) => (
                            <div key={r.id} data-testid={TID.adminCustomRoleRow(r.id)} className="panel px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="font-mono text-sm text-white">{r.name}</div>
                                    {r.description && <p className="text-[11px] text-slate-500 mt-0.5">{r.description}</p>}
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                        {r.modules.map((m) => (
                                            <span key={m} className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-cyan-500/30 text-cyan-300 rounded-sm">{m}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button data-testid={TID.adminCustomRoleEditBtn(r.id)} onClick={() => setEditingRole(r)} className="btn-ghost inline-flex items-center gap-1"><Edit3 size={11} /> Edit</button>
                                    <button data-testid={TID.adminCustomRoleDeleteBtn(r.id)} onClick={() => removeCustomRole(r)} className="btn-ghost text-rose-300 border-rose-500/30 hover:border-rose-500/60 inline-flex items-center gap-1"><Trash2 size={11} /> Delete</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Panel>

            {/* Edit drawer */}
            <EditUserDialog user={editing} onClose={() => setEditing(null)} isSuper={isSuper} customRoles={customRoles} onSaved={() => { setEditing(null); load(); }} />
            <CustomRoleDialog open={createRoleOpen} onOpenChange={setCreateRoleOpen} modulesList={modulesList} onSaved={load} />
            <CustomRoleDialog role={editingRole} open={!!editingRole} onOpenChange={(o) => !o && setEditingRole(null)} modulesList={modulesList} onSaved={load} />
        </div>
    );
}

function CreateUserDialog({ open, onOpenChange, isSuper, customRoles = [], onCreated }) {
    const [form, setForm] = useState({ email: "", name: "", mobile: "", password: "", roles: new Set(["Execution_All"]), admin: false, active: true });
    const [loading, setLoading] = useState(false);

    const reset = () => setForm({ email: "", name: "", mobile: "", password: "", roles: new Set(["Execution_All"]), admin: false, active: true });

    const toggleRole = (r) => {
        const next = new Set(form.roles);
        if (next.has(r)) next.delete(r); else next.add(r);
        setForm({ ...form, roles: next });
    };

    const submit = async () => {
        setLoading(true);
        try {
            const roles = Array.from(form.roles);
            if (form.admin && isSuper) roles.push("Admin");
            await api.post("/auth/users", {
                email: form.email.trim().toLowerCase(),
                name: form.name.trim(),
                mobile: form.mobile.trim(),
                password: form.password,
                roles,
                active: form.active,
            });
            toast.success("User created");
            onCreated?.();
            onOpenChange(false);
            reset();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <button data-testid="admin-create-user-btn" className="btn-primary flex items-center gap-1.5"><Plus size={12} /> New User</button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--bg-elevated)] border border-cyan-500/20 text-slate-200 max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-white">Create User</DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 font-mono">
                        Provision a workspace user and pick the execution roles that gate their engines and modules.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Full Name" testid="admin-create-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                        <Field label="Email" testid="admin-create-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                        <Field label="Mobile" testid="admin-create-mobile" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} />
                        <Field label="Initial Password" testid="admin-create-password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
                    </div>
                    <div>
                        <div className="label-eyebrow mb-2">Execution Roles</div>
                        <div className="flex flex-wrap gap-2">
                            {EXECUTION_ROLES.map((r) => {
                                const on = form.roles.has(r);
                                return (
                                    <button key={r} type="button" onClick={() => toggleRole(r)}
                                        className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border transition-colors ${on ? "border-cyan-400/60 text-cyan-300 bg-cyan-500/10" : "border-white/10 text-slate-400"}`}>
                                        {on && <CheckCircle2 size={9} className="inline mr-1" />}{r}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {customRoles.length > 0 && (
                        <div>
                            <div className="label-eyebrow mb-2">Custom Roles (Dynamic RBAC)</div>
                            <div className="flex flex-wrap gap-2">
                                {customRoles.map((cr) => {
                                    const on = form.roles.has(cr.name);
                                    return (
                                        <button key={cr.id} type="button" onClick={() => toggleRole(cr.name)}
                                            title={cr.modules.join(", ")}
                                            className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border transition-colors ${on ? "border-emerald-400/60 text-emerald-300 bg-emerald-500/10" : "border-white/10 text-slate-400"}`}>
                                            {on && <CheckCircle2 size={9} className="inline mr-1" />}{cr.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {isSuper && (
                        <label className="flex items-center gap-2 panel px-3 py-2 text-xs cursor-pointer">
                            <input
                                data-testid="admin-create-grant-admin"
                                type="checkbox"
                                checked={form.admin}
                                onChange={(e) => setForm({ ...form, admin: e.target.checked })}
                                className="accent-cyan-400"
                            />
                            <span>Also grant <span className="font-mono text-cyan-300">Admin</span> role (platform owner privilege)</span>
                        </label>
                    )}
                </div>
                <DialogFooter>
                    <button data-testid="admin-create-submit" onClick={submit} disabled={loading} className="btn-primary disabled:opacity-50">
                        {loading ? "Creating…" : "Create User"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditUserDialog({ user, onClose, isSuper, customRoles = [], onSaved }) {
    const [form, setForm] = useState({ name: "", mobile: "", roles: new Set(), active: true, password: "" });

    useEffect(() => {
        if (user) setForm({
            name: user.name, mobile: user.mobile || "",
            roles: new Set(user.roles.filter((r) => r !== "Admin")),
            adminGranted: user.roles.includes("Admin"),
            active: user.active, password: "",
        });
    }, [user]);

    if (!user) return null;

    const toggleRole = (r) => {
        const next = new Set(form.roles);
        if (next.has(r)) next.delete(r); else next.add(r);
        setForm({ ...form, roles: next });
    };

    const save = async () => {
        const roles = Array.from(form.roles);
        if (form.adminGranted) roles.push("Admin");
        const body = {
            name: form.name.trim(),
            mobile: form.mobile.trim(),
            roles,
            active: form.active,
        };
        if (form.password) body.password = form.password;
        try {
            await api.put(`/auth/users/${user.id}`, body);
            toast.success("User updated");
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    return (
        <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-[var(--bg-elevated)] border border-cyan-500/20 text-slate-200 max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-white">Edit User · {user.email}</DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 font-mono">
                        Update this user's name, roles and active status. Only super-admins may grant the Admin role.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                        <Field label="Mobile" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} />
                        <Field label="Reset Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="leave empty to keep" />
                        <label className="flex items-center gap-2 panel px-3 py-2 text-xs cursor-pointer">
                            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-cyan-400" />
                            <span>Active</span>
                        </label>
                    </div>
                    <div>
                        <div className="label-eyebrow mb-2">Execution Roles</div>
                        <div className="flex flex-wrap gap-2">
                            {EXECUTION_ROLES.map((r) => {
                                const on = form.roles.has(r);
                                return (
                                    <button key={r} type="button" onClick={() => toggleRole(r)}
                                        className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border transition-colors ${on ? "border-cyan-400/60 text-cyan-300 bg-cyan-500/10" : "border-white/10 text-slate-400"}`}>
                                        {on && <CheckCircle2 size={9} className="inline mr-1" />}{r}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {customRoles.length > 0 && (
                        <div>
                            <div className="label-eyebrow mb-2">Custom Roles (Dynamic RBAC)</div>
                            <div className="flex flex-wrap gap-2">
                                {customRoles.map((cr) => {
                                    const on = form.roles.has(cr.name);
                                    return (
                                        <button key={cr.id} type="button" onClick={() => toggleRole(cr.name)}
                                            title={cr.modules.join(", ")}
                                            className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border transition-colors ${on ? "border-emerald-400/60 text-emerald-300 bg-emerald-500/10" : "border-white/10 text-slate-400"}`}>
                                            {on && <CheckCircle2 size={9} className="inline mr-1" />}{cr.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {isSuper && (
                        <label className="flex items-center gap-2 panel px-3 py-2 text-xs cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!form.adminGranted}
                                onChange={(e) => setForm({ ...form, adminGranted: e.target.checked })}
                                className="accent-cyan-400"
                            />
                            <span>Grant <span className="font-mono text-cyan-300">Admin</span> role</span>
                        </label>
                    )}
                </div>
                <DialogFooter>
                    <button onClick={onClose} className="btn-ghost"><X size={12} /> Cancel</button>
                    <button data-testid="admin-edit-save" onClick={save} className="btn-primary flex items-center gap-1"><Save size={12} /> Save Changes</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Create OR edit a Dynamic RBAC custom role — same dialog, mode inferred
 * from whether a `role` (existing CustomRole) is passed in. */
function CustomRoleDialog({ role, open, onOpenChange, modulesList, onSaved }) {
    const isEdit = !!role;
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [modules, setModules] = useState(new Set());
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setName(role?.name || "");
            setDescription(role?.description || "");
            setModules(new Set(role?.modules || []));
        }
    }, [open, role]);

    const toggleModule = (key) => {
        const next = new Set(modules);
        next.has(key) ? next.delete(key) : next.add(key);
        setModules(next);
    };

    const submit = async () => {
        if (!isEdit && !name.trim()) { toast.error("Role name is required"); return; }
        if (modules.size === 0) { toast.error("Select at least one module"); return; }
        setLoading(true);
        try {
            if (isEdit) {
                await api.put(`/admin/custom-roles/${role.id}`, { modules: Array.from(modules), description });
                toast.success("Custom role updated");
            } else {
                await api.post("/admin/custom-roles", { name: name.trim(), modules: Array.from(modules), description });
                toast.success("Custom role created");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[var(--bg-elevated)] border border-cyan-500/20 text-slate-200 max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-white flex items-center gap-2">
                        <LayoutGrid size={16} className="text-cyan-400" /> {isEdit ? `Edit Role · ${role.name}` : "New Custom Role"}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 font-mono">
                        Custom roles scope a user to a specific set of platform modules — grants apply on their next request.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {!isEdit && (
                        <Field label="Role Name" testid={TID.adminCustomRoleNameInput} value={name} onChange={setName} placeholder="e.g. GRC_Reviewer" />
                    )}
                    <Field label="Description" value={description} onChange={setDescription} placeholder="What is this role for?" />
                    <div>
                        <div className="label-eyebrow mb-2">Modules Granted</div>
                        <div className="flex flex-wrap gap-2">
                            {modulesList.map((m) => {
                                const on = modules.has(m.key);
                                return (
                                    <button
                                        key={m.key}
                                        type="button"
                                        data-testid={TID.adminCustomRoleModuleCheckbox(m.key)}
                                        onClick={() => toggleModule(m.key)}
                                        title={m.label}
                                        className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border transition-colors ${on ? "border-cyan-400/60 text-cyan-300 bg-cyan-500/10" : "border-white/10 text-slate-400"}`}
                                    >
                                        {on && <CheckCircle2 size={9} className="inline mr-1" />}{m.key}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <button data-testid={TID.adminCustomRoleSubmit} onClick={submit} disabled={loading} className="btn-primary disabled:opacity-50">
                        {loading ? "Saving…" : isEdit ? "Save Changes" : "Create Role"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function Field({ label, value, onChange, type = "text", testid, placeholder }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1">{label}</label>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="field"
            />
        </div>
    );
}
